/**
 * Target parsing and validation utilities for the new target syntax:
 * [protocol]://[IP/IP Range/Host/DNS]:[port/s or range]/[URI]
 */

export interface ParsedTarget {
  protocol: string;
  host: string;
  port?: string;
  uri?: string;
  original: string;
  isValid: boolean;
  error?: string;
}

export interface TargetValidationResult {
  target: string;
  isValid: boolean;
  error?: string;
  parsed?: ParsedTarget;
}

/**
 * Parse a target string into its components
 */
export function parseTarget(target: string): ParsedTarget {
  const original = target.trim();
  
  if (!original) {
    return {
      protocol: '',
      host: '',
      original,
      isValid: false,
      error: 'Target cannot be empty'
    };
  }

  try {
    // Match the pattern: [protocol]://[host]:[port]/[uri]
    const regex = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^:/]+)(?::([^/]+))?(?:\/(.*))?$/;
    const match = original.match(regex);
    
    if (!match) {
      // Try to parse as legacy format and convert
      return parseLegacyTarget(original);
    }
    
    const [, protocol, host, port, uri] = match;
    
    // Validate protocol
    if (!isValidProtocol(protocol)) {
      return {
        protocol,
        host,
        port,
        uri,
        original,
        isValid: false,
        error: `Invalid protocol: ${protocol}`
      };
    }
    
    // Validate host
    if (!isValidHost(host)) {
      return {
        protocol,
        host,
        port,
        uri,
        original,
        isValid: false,
        error: `Invalid host: ${host}`
      };
    }
    
    // Validate port if present
    if (port && !isValidPort(port)) {
      return {
        protocol,
        host,
        port,
        uri,
        original,
        isValid: false,
        error: `Invalid port: ${port}`
      };
    }
    
    return {
      protocol,
      host,
      port,
      uri,
      original,
      isValid: true
    };
  } catch (error) {
    return {
      protocol: '',
      host: '',
      original,
      isValid: false,
      error: `Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Parse legacy target formats and convert to new format
 */
function parseLegacyTarget(target: string): ParsedTarget {
  const original = target.trim();
  
  // Check if it's already a URL
  if (original.includes('://')) {
    try {
      const url = new URL(original);
      return {
        protocol: url.protocol.replace(':', ''),
        host: url.hostname,
        port: url.port || undefined,
        uri: url.pathname + url.search + url.hash || undefined,
        original,
        isValid: true
      };
    } catch {
      // Fall through to other parsing attempts
    }
  }
  
  // Check if it's an IP address or hostname
  if (isValidHost(original)) {
    // Default to https for hostnames, http for IPs
    const protocol = /^\d+\.\d+\.\d+\.\d+/.test(original) ? 'http' : 'https';
    return {
      protocol,
      host: original,
      original,
      isValid: true
    };
  }
  
  // Check if it's a port range or single port
  if (/^\d+(-\d+)?$/.test(original)) {
    return {
      protocol: 'tcp',
      host: 'localhost',
      port: original,
      original,
      isValid: true
    };
  }
  
  return {
    protocol: '',
    host: '',
    original,
    isValid: false,
    error: 'Unable to parse target format'
  };
}

/**
 * Validate if a protocol is supported
 */
function isValidProtocol(protocol: string): boolean {
  const supportedProtocols = [
    'http', 'https', 'tcp', 'udp', 'ftp', 'sftp', 'ssh', 'telnet', 'smtp', 'pop3', 'imap'
  ];
  return supportedProtocols.includes(protocol.toLowerCase());
}

/**
 * Validate if a host is valid (IP, CIDR, or hostname)
 */
function isValidHost(host: string): boolean {
  // Check if it's a valid IP address
  if (isValidIP(host)) return true;
  
  // Check if it's a valid CIDR
  if (isValidCIDR(host)) return true;
  
  // Check if it's a valid hostname
  if (isValidHostname(host)) return true;
  
  return false;
}

/**
 * Check if string is a valid IP address
 */
function isValidIP(ip: string): boolean {
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
  return ipv4Regex.test(ip) || ipv6Regex.test(ip);
}

/**
 * Check if string is a valid CIDR
 */
function isValidCIDR(cidr: string): boolean {
  const cidrRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(?:[0-9]|[1-2][0-9]|3[0-2])$/;
  return cidrRegex.test(cidr);
}

/**
 * Check if string is a valid hostname
 */
function isValidHostname(hostname: string): boolean {
  const hostnameRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
  return hostnameRegex.test(hostname) && hostname.length <= 253;
}

/**
 * Validate if a port is valid (single port or range)
 */
function isValidPort(port: string): boolean {
  // Single port
  if (/^\d+$/.test(port)) {
    const num = parseInt(port, 10);
    return num >= 1 && num <= 65535;
  }
  
  // Port range
  if (/^\d+-\d+$/.test(port)) {
    const [start, end] = port.split('-').map(p => parseInt(p, 10));
    return start >= 1 && start <= 65535 && end >= 1 && end <= 65535 && start <= end;
  }
  
  return false;
}

/**
 * Format a parsed target back to the standard format
 */
export function formatTarget(parsed: ParsedTarget): string {
  if (!parsed.isValid) {
    return parsed.original;
  }
  
  let result = `${parsed.protocol}://${parsed.host}`;
  
  if (parsed.port) {
    result += `:${parsed.port}`;
  }
  
  if (parsed.uri) {
    result += `/${parsed.uri}`;
  }
  
  return result;
}

/**
 * Convert legacy targets to new format
 */
export function convertLegacyTargets(targets: string[]): string[] {
  return targets.map(target => {
    const parsed = parseTarget(target);
    return parsed.isValid ? formatTarget(parsed) : target;
  });
}

/**
 * Validate multiple targets
 */
export function validateTargets(targets: string[]): TargetValidationResult[] {
  return targets.map(target => {
    const parsed = parseTarget(target);
    return {
      target,
      isValid: parsed.isValid,
      error: parsed.error,
      parsed: parsed.isValid ? parsed : undefined
    };
  });
}

/**
 * Get a user-friendly description of the target format
 */
export function getTargetFormatDescription(): string {
  return 'Format: [protocol]://[IP/IP Range/Host/DNS]:[port/s or range]/[URI]\n\n' +
         'Examples:\n' +
         '• https://example.com:443/api/endpoint\n' +
         '• http://192.168.1.1:8080/\n' +
         '• tcp://10.0.0.0/8:22\n' +
         '• https://subdomain.example.com:80-443/path\n' +
         '• udp://192.168.1.100:53';
}

/**
 * Generate placeholder text for target input
 */
export function getTargetPlaceholder(): string {
  return 'https://example.com:443/api/endpoint';
}
